import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { ProgressBar } from 'primereact/progressbar';
import { Link } from '@tanstack/react-router';
import { HoverTooltip } from './common/HoverTooltip';
import { ReactNode, useState, useMemo, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { useQuery } from '@apollo/client';
import {
  portfolioIdAtom,
  latestSnapshotIdAtom,
  triggerHardRefetchAtom,
} from '~/atoms/dashboardAtom';
import { GET_AI_ANALYSIS } from '~/graphql/queries/portfolio';
import { GET_EQALLOCATION_DATA } from '~/graphql/queries/eqallocation';
import { AUMConfirmationDialog } from './common/AUMConfirmationDialog';
import { usePortfolioAnalysis } from '~/hooks/usePortfolioAnalysis';
import { getAnalysisDisplayState } from '~/utils/analysisStatusDisplay';
import { NotificationBell } from '~/components/ui/layout/page/NotificationBell';
import { UserWidget } from '~/components/ui/layout/page/UserWidget';
import { FeedbackDialog } from '~/components/ui/layout/page/FeedbackDialog';
import { HeaderMenu } from '~/components/ui/layout/page/HeaderMenu';
import { canAccess } from '~/utils/rbac';
import { EditPortfolioDialog } from '~/components/ui/common/EditPortfolioDialog';
import { useRBAC } from '~/hooks/useRBAC';
import { CreatePortfolioStepperDialog } from '~/components/ui/common/CreatePortfolioStepperDialog';
import { useSidebar } from '~/routes/_authLayout';
import '~/styles/component/bq-app-header.css';
import { ThemeToggle } from '~/components/ui/theme/ThemeToggle';

interface AppHeaderProps {
  children?: ReactNode;
  onResetLayout?: () => void;
  isDashboard?: boolean;
}

export const AppHeader = ({
  children,
  onResetLayout,
  isDashboard = false,
}: AppHeaderProps) => {
  const portfolioId = useAtomValue(portfolioIdAtom);
  const snapshotId = useAtomValue(latestSnapshotIdAtom);
  const triggerHardRefetch = useAtomValue(triggerHardRefetchAtom);
  const { canCreate } = useRBAC();
  const [editPortfolioVisible, setEditPortfolioVisible] = useState(false);
  const [feedbackDialogVisible, setFeedbackDialogVisible] = useState(false);
  const [createPortfolioVisible, setCreatePortfolioVisible] = useState(false);
  const [showUpdateButton, setShowUpdateButton] = useState(true);
  const [progressDismissed, setProgressDismissed] = useState(false);

  let sidebarWidth = 56;
  try {
    const sidebar = useSidebar();
    sidebarWidth = sidebar.sidebarWidth;
  } catch {
    // If not in sidebar context, use default
  }

  const {
    data: aiAnalysisData,
    loading: analysisLoading,
    refetch,
  } = useQuery(GET_AI_ANALYSIS, {
    variables: { snapshotId, sectors: [] },
    skip: !snapshotId,
    fetchPolicy: 'cache-and-network',
  });

  // Check if reference universe is uploaded — use network-only to always get fresh data
  // (matches the upload page's PlainTableWidgetWrapper fetch policy)
  const { data: allocationData } = useQuery(GET_EQALLOCATION_DATA, {
    variables: { snapshotId },
    skip: !snapshotId,
    fetchPolicy: 'network-only',
  });

  const hasReferenceUniverse = useMemo(() => {
    return (
      allocationData?.universe &&
      allocationData.universe.length > 0 &&
      !!allocationData.universe[0]?.allocation?.id
    );
  }, [allocationData]);

  // Refetch when analysis completes
  useEffect(() => {
    if (triggerHardRefetch > 0 && snapshotId) {
      refetch({ snapshotId, sectors: [] });
    }
  }, [triggerHardRefetch, refetch, snapshotId]);

  const {
    isAnalyzing,
    analysisProgress,
    analysisStatusValue,
    aumDialogVisible,
    currentAUM,
    currency,
    isUpdatingAUM,
    handleReAnalyse,
    handleAUMConfirm,
    setAumDialogVisible,
  } = usePortfolioAnalysis({ snapshotId: snapshotId || undefined });

  // Reset progress dismissed state when new analysis starts
  useEffect(() => {
    if (isAnalyzing && analysisProgress === 0) {
      setProgressDismissed(false);
    }
  }, [isAnalyzing, analysisProgress]);

  // Check if analysis is complete or failed
  const isFailed = analysisStatusValue === 'failed';
  const isComplete = analysisProgress === 100 && !isFailed;
  const isTerminal = isComplete || isFailed;

  // Hide button when analyzing (and not terminal), show it otherwise
  useEffect(() => {
    if (isAnalyzing && !isTerminal) {
      // Hide button when analysis is in progress
      setShowUpdateButton(false);
    } else {
      // Show button when not analyzing or when analysis is complete/failed
      setShowUpdateButton(true);
    }
  }, [isAnalyzing, isTerminal]);

  const formattedTimestamp = useMemo(() => {
    const timestamp = aiAnalysisData?.EQPortfoliosAIAnalysis?.[0]?.createdAt;

    if (!timestamp) return null;

    try {
      const hasTimezoneInfo = /[+-]\d{2}:\d{2}$|Z$/i.test(timestamp);
      const utcTimestamp = hasTimezoneInfo ? timestamp : `${timestamp}Z`;
      const date = new Date(utcTimestamp);

      if (isNaN(date.getTime())) {
        return null;
      }

      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return null;
    }
  }, [aiAnalysisData]);

  const showAnalysisControls = portfolioId && snapshotId;

  return (
    <>
      <div
        className="header-container flex justify-content-between align-items-center"
        style={{
          left: `${sidebarWidth}px`,
          width: `calc(100% - ${sidebarWidth}px)`,
          paddingLeft: '0.75rem',
          paddingRight: '1rem',
          boxSizing: 'border-box',
        }}
      >
        <div
          className="breadcrumb-container"
          style={{ flex: '0 1 auto', minWidth: 0, overflow: 'hidden' }}
        >
          {children}
        </div>

        {showAnalysisControls && (
          <div className="flex align-items-center gap-3 ml-auto mr-3">
            <div
              className="flex align-items-center gap-2 px-3 py-2 border-round"
              style={{
                backgroundColor: 'var(--bq-surface-muted)',
                border: '1px solid var(--bq-border-muted)',
                color: 'var(--bq-text-primary)',
              }}
            >
              <i
                className="pi pi-clock"
                style={{
                  fontSize: '0.875rem',
                  color: 'var(--bq-text-secondary)',
                }}
              />
              <span
                className="text-sm"
                style={{ color: 'var(--bq-text-secondary)' }}
              >
                Last Analysis:
              </span>
              {analysisLoading ? (
                <Skeleton width="100px" height="1rem" />
              ) : formattedTimestamp ? (
                <span
                  className="text-sm font-semibold"
                  style={{ color: 'var(--bq-text-primary)' }}
                >
                  {formattedTimestamp}
                </span>
              ) : (
                <span
                  className="text-sm"
                  style={{ color: 'var(--bq-text-secondary)' }}
                >
                  Not yet analyzed
                </span>
              )}
            </div>

            {isAnalyzing &&
              !progressDismissed &&
              (() => {
                const displayState = getAnalysisDisplayState({
                  analysisProgress,
                  analysisStatusValue,
                });

                // Determine state
                const isFailed = analysisStatusValue === 'failed';
                const isComplete = analysisProgress === 100 && !isFailed;
                const colorAttr = isFailed
                  ? 'red'
                  : isComplete
                    ? 'green'
                    : 'blue';

                return (
                  <div
                    className="analysis-progress-container flex align-items-center gap-3 p-2 border-round"
                    style={{
                      backgroundColor: displayState.backgroundColor,
                      border: `1px solid ${displayState.borderColor}`,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    data-state={
                      isFailed
                        ? 'failed'
                        : isComplete
                          ? 'complete'
                          : 'analyzing'
                    }
                    data-color={colorAttr}
                  >
                    <div
                      className="flex align-items-center gap-2"
                      style={{ position: 'relative', zIndex: 1 }}
                    >
                      <i
                        className={displayState.icon}
                        style={{
                          fontSize: '1rem',
                          color: displayState.iconColor,
                        }}
                      />
                      <span
                        className="text-base font-semibold"
                        style={{ color: displayState.textColor }}
                      >
                        {displayState.message}
                      </span>
                    </div>
                    <div
                      className="flex align-items-center gap-2"
                      style={{
                        width: '160px',
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      <ProgressBar
                        value={analysisProgress}
                        showValue={false}
                        style={{ height: '8px', flex: 1 }}
                        color={displayState.progressBarColor}
                      />
                      <span
                        className="text-sm font-bold"
                        style={{ color: 'var(--bq-text-primary)' }}
                      >
                        {Math.round(analysisProgress)}%
                      </span>
                    </div>
                    {(isComplete || isFailed) && (
                      <i
                        className="pi pi-times cursor-pointer transition-colors transition-duration-200"
                        onClick={() => setProgressDismissed(true)}
                        style={{
                          fontSize: '0.875rem',
                          position: 'relative',
                          zIndex: 1,
                          marginLeft: '0.5rem',
                          padding: '0.25rem',
                          color: 'var(--bq-text-tertiary)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color =
                            'var(--bq-text-primary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color =
                            'var(--bq-text-tertiary)';
                        }}
                        title="Dismiss"
                      />
                    )}
                  </div>
                );
              })()}

            {showUpdateButton && (
              <HoverTooltip
                content={
                  !hasReferenceUniverse ? (
                    portfolioId ? (
                      <span>
                        Reference universe is required to run portfolio
                        analysis. Upload it from the{' '}
                        <Link
                          to="/portfolio/$portfolioId/upload"
                          params={{ portfolioId }}
                          style={{
                            color: '#60a5fa',
                            fontWeight: 600,
                            textDecoration: 'underline',
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Upload page
                        </Link>
                        .
                      </span>
                    ) : (
                      'Reference universe is required to run portfolio analysis. You can upload it from the Upload page (portfolio → Upload in the sidebar).'
                    )
                  ) : null
                }
                position="bottom"
                disabled={hasReferenceUniverse}
              >
                <span className="update-analysis-btn-wrap inline-block">
                  <Button
                    label="Update Analysis"
                    icon="pi pi-refresh"
                    onClick={handleReAnalyse}
                    disabled={!hasReferenceUniverse}
                    style={{
                      padding: '0.4375rem 0.65625rem',
                      fontSize: '0.875rem',
                      ...(!hasReferenceUniverse && {
                        opacity: 0.6,
                        cursor: 'not-allowed',
                        pointerEvents: 'none',
                      }),
                    }}
                    className={
                      !hasReferenceUniverse ? 'p-button-disabled' : undefined
                    }
                  />
                </span>
              </HoverTooltip>
            )}
          </div>
        )}

        <div
          className="header-actions"
          style={{
            flex: '0 0 auto',
            display: 'flex',
            gap: '0.75rem',
            minWidth: 0,
            width: 'auto',
          }}
        >
          <HeaderMenu
            onResetLayout={onResetLayout}
            onEditPortfolio={() => setEditPortfolioVisible(true)}
            onSendFeedback={() => setFeedbackDialogVisible(true)}
            onCreatePortfolio={
              canCreate('portfolio')
                ? () => setCreatePortfolioVisible(true)
                : undefined
            }
            isDashboard={isDashboard}
            portfolioId={portfolioId || undefined}
          />
          {canAccess('workflow') && <NotificationBell />}
          <ThemeToggle />
          <UserWidget />
        </div>
      </div>

      <EditPortfolioDialog
        visible={editPortfolioVisible}
        onHide={() => setEditPortfolioVisible(false)}
      />

      <FeedbackDialog
        visible={feedbackDialogVisible}
        onHide={() => setFeedbackDialogVisible(false)}
      />

      <CreatePortfolioStepperDialog
        visible={createPortfolioVisible}
        onHide={() => setCreatePortfolioVisible(false)}
      />

      {showAnalysisControls && (
        <AUMConfirmationDialog
          visible={aumDialogVisible}
          onHide={() => setAumDialogVisible(false)}
          onConfirm={handleAUMConfirm}
          snapshotAUM={currentAUM}
          currency={currency}
          isLoading={isUpdatingAUM || isAnalyzing}
        />
      )}
    </>
  );
};
